import firebase from "@firebase/app";
import { auth, functions } from "../firebase";
import _ from "lodash";
import {
  clearStoredVerifyToken,
  isUserVerified,
  persistAuthUser,
  readStoredVerifyToken
} from "@/helpers/onboard";

const state = {
  userToken: JSON.parse(localStorage.getItem("userToken")),
  userClaims: JSON.parse(localStorage.getItem("userClaims"))
}

const getters = {
  userToken: state => state.userToken,
  userClaims: state => state.userClaims || {},
  userId: state => _.get(state, "userToken.uid", null),
  userEmail: state => _.get(state, "userToken.email", null),
  isAuthenticated: state => !!state.userToken,
  isVerified: state => {
    if (auth.currentUser && isUserVerified(auth.currentUser)) return true;
    return isUserVerified(state.userToken);
  },
  tenantId: (_state, getters) => getters.userClaims.tenantId,
  userSetupCompleted: (_state, getters) => getters.tenantId && getters.userClaims.storeId
};

const sendVerificationEmail = functions.httpsCallable("sendVerificationEmail");
const verifyUser = functions.httpsCallable("verifyEmail");
const sendPasswordResetEmail = functions.httpsCallable(
  "sendPasswordResetEmail"
);
const resetPassword = functions.httpsCallable("resetPassword");
const updateUser = functions.httpsCallable("updateUser");
const activateUserAccount = functions.httpsCallable("activateUserAccount");

const actions = {
  observeUser(context) {
    auth.onAuthStateChanged(async user => {
      if (user) {
        context.commit("setUserToken", user);
        try {
          const { claims } = await auth.currentUser.getIdTokenResult();
          context.commit("setUserClaims", claims);
          await context.dispatch("fetchSubscription");
        } catch (error) {
          /* claims can wait */
        }
        const pending = readStoredVerifyToken();
        if (pending && !isUserVerified(auth.currentUser)) {
          try {
            await context.dispatch("verifyUser", { token: pending });
          } catch (error) {
            /* /start will ask again if the token is still needed */
          }
        }
      } else {
        context.commit("resetUserToken");
        context.commit("resetUserClaims");
        context.commit("setSubscription", null);
        context.commit("setSubscriptionLoaded", false);
      }
    });
  },
  async register({ dispatch }, payload) {
    await auth.createUserWithEmailAndPassword(payload.email, payload.password);
    await auth.currentUser.updateProfile({ displayName: payload.name });

    dispatch("sendVerificationEmail");
  },
  sendVerificationEmail(context) {
    const userToken = context.getters.userToken;

    if (!userToken || (userToken && userToken.emailVerified)) return;

    return sendVerificationEmail({
      displayName: userToken.displayName
    });
  },
  async verifyUser(context, opts) {
    const token = (opts && opts.token) || readStoredVerifyToken();
    if (!token) return { data: { success: false } };
    if (auth.currentUser && auth.currentUser.emailVerified) {
      clearStoredVerifyToken();
      context.commit("setUserToken", persistAuthUser(auth.currentUser));
      return { data: { success: true } };
    }

    let response;
    try {
      response = await verifyUser({ token });
    } catch (error) {
      const code = String((error && error.code) || "");
      const message = String((error && error.message) || "");
      if (
        code.indexOf("failed-precondition") !== -1 ||
        message.indexOf("Invalid or expired") !== -1
      ) {
        clearStoredVerifyToken();
      }
      throw error;
    }
    const data = response && response.data;
    const ok = !!(data && data !== false && data.success !== false);
    if (!ok) {
      throw new Error(
        (data && data.message) || "Could not confirm that email."
      );
    }

    if (auth.currentUser) {
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
      context.commit("setUserToken", persistAuthUser(auth.currentUser));
    }

    if (auth.currentUser && auth.currentUser.emailVerified) {
      clearStoredVerifyToken();
    }

    return response;
  },
  async login(context, payload) {
    const { user } = await auth.signInWithEmailAndPassword(payload.email, payload.password);
    context.commit("setUserToken", user);
  },
  async loginWithGoogle(context) {
    const provider = new firebase.auth.GoogleAuthProvider();
    const { user } = await auth.signInWithPopup(provider);
    context.commit("setUserToken", user);
  },
  async refreshUserClaims(context) {
    if (!auth.currentUser) return;
    const { claims } = await auth.currentUser.getIdTokenResult(true);
    context.commit("setUserClaims", claims);
  },
  async reloadCurrentUser(context) {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    context.commit("setUserToken", persistAuthUser(auth.currentUser));
  },
  async activateUserAccount(context, payload) {
    await activateUserAccount(payload);
    await context.dispatch('refreshUserClaims');
  },
  async logout() {
    await auth.signOut();
  },
  sendPasswordResetEmail(context, opts) {
    return sendPasswordResetEmail(opts);
  },
  resetPassword(context, opts) {
    return resetPassword(opts);
  },
  async changePassword(context, { password, newPassword }) {
    await auth.currentUser.reauthenticateWithCredential(
      firebase.auth.EmailAuthProvider.credential(
        context.getters.userEmail,
        password
      )
    );

    return auth.currentUser.updatePassword(newPassword);
  },
  async updateUser(context, user) {
    const { displayName, email, password } = user;

    await auth.currentUser.reauthenticateWithCredential(
      firebase.auth.EmailAuthProvider.credential(
        context.getters.userEmail,
        password
      )
    );

    await updateUser({
      user: {
        displayName,
        email
      }
    });

    await auth.currentUser.reauthenticateWithCredential(
      firebase.auth.EmailAuthProvider.credential(email, password)
    );
  }
};

const mutations = {
  setUserToken(state, user) {
    const snap = persistAuthUser(user) || user;
    localStorage.setItem("userToken", JSON.stringify(snap));
    state.userToken = snap;
  },
  resetUserToken(state) {
    state.userToken = null;
    localStorage.removeItem("userToken");
  },
  setUserClaims(state, userClaims) {
    localStorage.setItem("userClaims", JSON.stringify(userClaims));
    state.userClaims = userClaims;
  },
  resetUserClaims(state) {
    state.userClaims = null;
    localStorage.removeItem("userClaims");
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
